import { Button, Input } from "@aix/ui"
import { useNavigate, useParams, useSearch } from "@tanstack/react-router"
import { ArrowLeft, MessageCircle, Send, ThumbsDown, ThumbsUp } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import ReactPlayer from "react-player"
import toast from "react-hot-toast"
import { trpc } from "../../lib/trpc"

export default function VideoPlayerScreen() {
  const navigate = useNavigate()
  const { videoId } = useParams({ strict: false }) as { videoId: string }
  const search = useSearch({ strict: false }) as { journeyId?: string }

  const [currentTime, setCurrentTime] = useState(0)
  const [showChat, setShowChat] = useState(true) // Show chat by default
  const [questionText, setQuestionText] = useState("")
  const [playing, setPlaying] = useState(false)
  const playerRef = useRef<ReactPlayer>(null)
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const { data: video, isLoading } = trpc.videoOnboarding.getVideo.useQuery({ videoId })
  const updateProgressMutation = trpc.videoOnboarding.updateVideoProgress.useMutation({
    onError: (error) => {
      console.error("Failed to update progress:", error)
      // Silent fail for progress updates to avoid annoying users
    },
  })
  const askQuestionMutation = trpc.videoOnboarding.askQuestion.useMutation({
    onError: (error) => {
      toast.error(error.message || "Failed to ask question")
    },
  })
  const rateAnswerMutation = trpc.videoOnboarding.rateAnswer.useMutation({
    onError: (error) => {
      toast.error(error.message || "Failed to rate answer")
    },
  })

  const [chatHistory, setChatHistory] = useState<
    {
      questionId?: string
      question: string
      answer: string
      citations?: { videoId: string; title: string }[]
      rated?: "thumbs_up" | "thumbs_down"
    }[]
  >([])

  // Track progress when video plays
  const handleProgress = (state: { played: number; playedSeconds: number }) => {
    setCurrentTime(state.playedSeconds)

    if (!video?.duration) return

    const completionPercentage = Math.min(Math.round(state.played * 100), 100)

    // Update progress every 5 seconds (throttle updates)
    if (Math.floor(state.playedSeconds) % 5 === 0) {
      updateProgressMutation.mutate({
        videoId,
        lastPosition: Math.floor(state.playedSeconds),
        completionPercentage,
      })
    }
  }

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
      }
    }
  }, [])

  const handleAskQuestion = async () => {
    if (!questionText.trim()) return

    try {
      const result = await askQuestionMutation.mutateAsync({
        questionText,
        videoId,
      })

      setChatHistory((prev) => [
        ...prev,
        {
          questionId: result.questionId,
          question: questionText,
          answer: result.answer,
          citations: result.citations,
        },
      ])

      setQuestionText("")
    } catch (error) {
      // Error already handled by mutation onError
    }
  }

  const handleRateAnswer = async (questionId: string, rating: "thumbs_up" | "thumbs_down") => {
    try {
      await rateAnswerMutation.mutateAsync({ questionId, rating })
      setChatHistory((prev) => prev.map((item) => (item.questionId === questionId ? { ...item, rated: rating } : item)))
    } catch (error) {
      // Error already handled by mutation onError
    }
  }

  const jumpToTimestamp = (seconds: number) => {
    if (playerRef.current) {
      playerRef.current.seekTo(seconds, "seconds")
      setPlaying(true)
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-primary mb-4">Video not found</h2>
          <Button
            onClick={() =>
              search.journeyId
                ? navigate({ to: "/onboarding/journey/$journeyId", params: { journeyId: search.journeyId } })
                : navigate({ to: "/onboarding/journeys" })
            }
          >
            Back
          </Button>
        </div>
      </div>
    )
  }

  const transcriptSegments = video.transcriptData?.segments || []

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-secondary border-b border-border p-4">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button
            onClick={() =>
              search.journeyId
                ? navigate({ to: "/onboarding/journey/$journeyId", params: { journeyId: search.journeyId } })
                : navigate({ to: "/onboarding/journeys" })
            }
            className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-primary">{video.title}</h1>
          </div>
          <Button variant="outline" onClick={() => setShowChat(!showChat)}>
            <MessageCircle className="w-4 h-4 mr-2" />
            {showChat ? "Hide Chat" : "Ask Question"}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Video Player */}
          <div className="lg:col-span-2 space-y-6">
            {/* Universal Video Player (Loom, YouTube, Vimeo, MP4) */}
            <div className="bg-secondary rounded-lg overflow-hidden" style={{ aspectRatio: "16/9" }}>
              <ReactPlayer
                ref={playerRef}
                url={video.videoUrl}
                playing={playing}
                controls
                width="100%"
                height="100%"
                onProgress={handleProgress}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                config={{
                  youtube: {
                    playerVars: { showinfo: 1 },
                  },
                  vimeo: {
                    playerOptions: { byline: false },
                  },
                }}
              />
            </div>

            {/* Video Description */}
            {video.description && (
              <div className="bg-secondary rounded-lg p-6">
                <h2 className="text-lg font-semibold text-primary mb-3">About this video</h2>
                <p className="text-muted-foreground">{video.description}</p>
              </div>
            )}

            {/* Transcript */}
            {transcriptSegments.length > 0 && (
              <div className="bg-secondary rounded-lg p-6">
                <h2 className="text-lg font-semibold text-primary mb-4">Transcript</h2>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {transcriptSegments.map((segment, index) => (
                    <div key={index} className="flex gap-3 hover:bg-background p-2 rounded transition-colors">
                      <button
                        onClick={() => jumpToTimestamp(segment.start)}
                        className="text-sm text-primary hover:underline font-mono min-w-[60px]"
                      >
                        {formatTime(segment.start)}
                      </button>
                      <p className="text-sm text-muted-foreground">{segment.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Chatbot (if shown) */}
          {showChat && (
            <div className="lg:col-span-1">
              <div className="bg-secondary rounded-lg p-6 sticky top-6">
                <h2 className="text-lg font-semibold text-primary mb-4">Ask a Question</h2>

                {/* Chat History */}
                <div className="space-y-4 mb-4 max-h-96 overflow-y-auto">
                  {chatHistory.map((item, index) => (
                    <div key={index} className="space-y-2">
                      {/* User Question */}
                      <div className="bg-primary text-primary-foreground rounded-lg p-3">
                        <p className="text-sm">{item.question}</p>
                      </div>

                      {/* Bot Answer */}
                      <div className="bg-background rounded-lg p-3">
                        <p className="text-sm text-muted-foreground mb-2">{item.answer}</p>

                        {/* Citations */}
                        {item.citations && item.citations.length > 0 && (
                          <div className="text-xs text-muted-foreground mb-2">
                            <p className="font-semibold mb-1">Sources:</p>
                            {item.citations.map((citation) => (
                              <button
                                key={citation.videoId}
                                onClick={() =>
                                  navigate({ to: "/onboarding/video/$videoId", params: { videoId: citation.videoId } })
                                }
                                className="block hover:underline text-primary"
                              >
                                • {citation.title}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Rating Buttons */}
                        {item.questionId && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleRateAnswer(item.questionId, "thumbs_up")}
                              className={`p-1 rounded transition-colors ${
                                item.rated === "thumbs_up"
                                  ? "bg-green-500 text-white"
                                  : "hover:bg-accent text-muted-foreground"
                              }`}
                              disabled={rateAnswerMutation.isLoading}
                            >
                              <ThumbsUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRateAnswer(item.questionId, "thumbs_down")}
                              className={`p-1 rounded transition-colors ${
                                item.rated === "thumbs_down"
                                  ? "bg-red-500 text-white"
                                  : "hover:bg-accent text-muted-foreground"
                              }`}
                              disabled={rateAnswerMutation.isLoading}
                            >
                              <ThumbsDown className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Input */}
                <div className="flex gap-2">
                  <Input
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    placeholder="Ask a question..."
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        void handleAskQuestion()
                      }
                    }}
                  />
                  <Button onClick={handleAskQuestion} loading={askQuestionMutation.isLoading}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
